/**
 * Stubbed Offline Sync Service
 * Will be replaced with proper offline queue when backend is connected.
 *
 * Aquí ya NO se encola nada. `queueClockIn`/`queueClockOut` —y con ellas
 * `addToQueue`— se borraron porque no las llamaba nadie: el reloj escribe
 * directo contra `database.ts`. Dejarlas invitaba a cablearlas sin volver a
 * pensar el problema. Lo que queda es solo el DESAGÜE: al recuperar conexión
 * se vacía lo que dejaran en `localStorage` versiones anteriores de la app.
 *
 * La historia de trabajo sin conexión está aplazada a propósito, no perdida
 * (ver MASTER-REMEDIATION-PLAN 10.6). Cuando se retome, el escritor se vuelve
 * a añadir aquí junto con el resto de pantallas que guardan datos, no solo el
 * reloj — que es lo que `OfflineBadge` ya le promete al usuario.
 */

import { addTimeLogDoc, updateTimeLogDoc } from './database';

const STORAGE_KEY = '2bros_offline_queue';
const CURRENT_SCHEMA_VERSION = 2;

interface OfflineAction {
    type: 'CLOCK_IN' | 'CLOCK_OUT';
    id: string;
    data: any;
    timestamp: number;
    schemaVersion?: number;
}

export function initOfflineSync(): void {
    window.addEventListener('online', processQueue);
    if (navigator.onLine) {
        processQueue();
    }
}

export function getQueueLength(): number {
    return getQueue().length;
}

function getQueue(): OfflineAction[] {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
}

function saveQueue(queue: OfflineAction[]) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
}

async function processQueue() {
    if (!navigator.onLine) return;

    const queue = getQueue();
    if (queue.length === 0) return;

    const newQueue: OfflineAction[] = [];

    for (const action of queue) {
        try {
            // Drop pre-v2 queued mutations: identity_v2 needs allowed_user_id stamped
            // at clock-in time from live auth, which we can't reconstruct from old payloads.
            if ((action.schemaVersion ?? 1) < CURRENT_SCHEMA_VERSION) {
                console.warn('[offlineSync] dropping pre-v2 queued action; please redo the clock action:', action.type);
                continue;
            }
            if (action.type === 'CLOCK_IN') {
                await addTimeLogDoc(action.data);
            } else if (action.type === 'CLOCK_OUT') {
                await updateTimeLogDoc(action.id, action.data);
            }
        } catch (error) {
            console.error('Failed to process offline action:', error);
            newQueue.push(action);
        }
    }

    saveQueue(newQueue);
    window.dispatchEvent(new CustomEvent('offline-queue-updated', { detail: newQueue.length }));
}
